import React, { useEffect, useState, useCallback } from "react";
import { Loader2, PackageSearch } from "lucide-react";
import { api, formatMoney } from "@/lib/api";
import OrderStatusBadge from "@/components/OrderStatusBadge";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [takenBy, setTakenBy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = takenBy ? { taken_by: takenBy } : {};
    Promise.all([
      api.get("/orders", { params }),
      api.get("/meta/order-statuses"),
      api.get("/salesmen"),
    ])
      .then(([o, s, sm]) => { setOrders(o.data); setStatuses(s.data.statuses); setSalesmen(sm.data); })
      .finally(() => setLoading(false));
  }, [takenBy]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    const { data } = await api.put(`/orders/${id}/status`, { status });
    setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
  };

  return (
    <div data-testid="admin-orders">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">{orders.length} order(s)</p>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500">Salesman</label>
          <select
            data-testid="orders-salesman-filter"
            value={takenBy}
            onChange={(e) => setTakenBy(e.target.value)}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-blue-500"
          >
            <option value="">All orders</option>
            {salesmen.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.order_count})</option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center text-slate-400">
          <PackageSearch className="h-10 w-10" />
          <p className="text-sm">No customer orders yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-xl border border-slate-100 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-3">
                <div className="min-w-0">
                  <p data-testid={`order-customer-name-${o.id}`} className="truncate text-sm font-semibold text-slate-800">
                    {o.shop_name ? <>{o.shop_name} <span className="font-normal text-slate-400">· {o.customer_name || "—"}</span></> : (o.customer_name || "—")}
                  </p>
                  <p data-testid={`order-customer-phone-${o.id}`} className="mt-0.5 text-xs text-slate-500">
                    📱 {o.customer_phone || "—"}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
                    <span data-testid={`order-id-${o.id}`}>Order #{o.id.slice(-6).toUpperCase()}</span>
                    <span data-testid={`order-date-${o.id}`}>{new Date(o.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  {o.taken_by && (
                    <p data-testid={`order-taken-by-${o.id}`} className="mt-1 inline-flex items-center gap-1 rounded-md bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                      🧑‍💼 Order Taken By: {o.taken_by}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <OrderStatusBadge status={o.status} />
                  <select
                    data-testid={`order-status-select-${o.id}`}
                    value={o.status}
                    onChange={(e) => changeStatus(o.id, e.target.value)}
                    className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-blue-500"
                  >
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {(() => {
                const paidItems = (o.items || []).filter((it) => !it.is_free);
                const freeItems = (o.items || []).filter((it) => it.is_free);
                const freeByProduct = freeItems.reduce((acc, f) => {
                  const key = f.product_id || "_";
                  (acc[key] = acc[key] || []).push(f);
                  return acc;
                }, {});
                const looseFree = freeByProduct["_"] || [];
                const totalDiscount = paidItems.reduce((s, it) => s + (it.offer_discount || 0), 0);
                const subtotal = paidItems.reduce((s, it) => s + (it.line_total || 0) + (it.offer_discount || 0), 0);
                const cleanFreeName = (n) => (n || "").replace(/\s*\(FREE\)\s*$/i, "").trim();
                return (
                  <>
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full min-w-[520px] text-sm">
                        <thead>
                          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                            <th className="pb-2 pr-3">Product</th>
                            <th className="pb-2 pr-3">Unit</th>
                            <th className="pb-2 pr-3 text-center">Qty</th>
                            <th className="pb-2 pr-3 text-right">Unit Price</th>
                            <th className="pb-2 text-right">Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paidItems.map((it, idx) => (
                            <React.Fragment key={idx}>
                              <tr data-testid={`order-item-${o.id}-${idx}`} className="border-t border-slate-50 text-slate-600">
                                <td className="py-1.5 pr-3">{it.name}</td>
                                <td className="py-1.5 pr-3">{it.unit || "—"}</td>
                                <td className="py-1.5 pr-3 text-center">{it.qty}</td>
                                <td className="py-1.5 pr-3 text-right">{formatMoney(it.price) || "—"}</td>
                                <td className="py-1.5 text-right font-medium">{formatMoney(it.line_total) || "—"}</td>
                              </tr>
                              {(freeByProduct[it.product_id] || []).map((f, fi) => (
                                <tr key={`f-${idx}-${fi}`} data-testid={`order-free-item-${o.id}-${idx}-${fi}`} className="text-emerald-700">
                                  <td className="py-1 pr-3 pl-4" colSpan={4}>
                                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium">
                                      🎁 {f.qty} {f.unit ? `${f.unit} ` : ""}{cleanFreeName(f.name)} — FREE
                                    </span>
                                  </td>
                                  <td className="py-1 text-right text-xs font-semibold">₹0</td>
                                </tr>
                              ))}
                              {it.offer_discount ? (
                                <tr key={`d-${idx}`} data-testid={`order-line-discount-${o.id}-${idx}`} className="text-amber-700">
                                  <td className="py-1 pr-3 pl-4 text-xs font-medium" colSpan={4}>Cash Discount</td>
                                  <td className="py-1 text-right text-xs font-semibold">-{formatMoney(it.offer_discount)}</td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          ))}
                          {looseFree.map((f, fi) => (
                            <tr key={`lf-${fi}`} data-testid={`order-free-item-loose-${o.id}-${fi}`} className="border-t border-slate-50 text-emerald-700">
                              <td className="py-1 pr-3" colSpan={4}>
                                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium">
                                  🎁 {f.qty} {f.unit ? `${f.unit} ` : ""}{cleanFreeName(f.name)} — FREE
                                </span>
                              </td>
                              <td className="py-1 text-right text-xs font-semibold">₹0</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
                      {totalDiscount > 0 ? (
                        <>
                          <div className="flex justify-between text-slate-500">
                            <span>Subtotal</span>
                            <span data-testid={`order-subtotal-${o.id}`}>{formatMoney(subtotal)}</span>
                          </div>
                          <div className="flex justify-between text-amber-700">
                            <span>Cash Discount</span>
                            <span data-testid={`order-cash-discount-${o.id}`}>-{formatMoney(totalDiscount)}</span>
                          </div>
                        </>
                      ) : null}
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>Grand Total</span>
                        <span data-testid={`order-total-${o.id}`} className="text-blue-700">{formatMoney(o.total) || "—"}</span>
                      </div>
                    </div>
                  </>
                );
              })()}
              {o.note ? <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Note: {o.note}</p> : null}
              {o.cancel_reason ? <p className="mt-2 text-xs font-medium text-red-600">Cancellation reason: {o.cancel_reason}</p> : null}
              {(o.edit_history || []).length > 0 && (
                <details data-testid={`admin-order-history-${o.id}`} className="mt-2">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-500 hover:text-slate-700">Edit history ({o.edit_history.length})</summary>
                  <div className="mt-2 space-y-2">
                    {o.edit_history.map((h, i) => (
                      <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs text-slate-600">
                        <p className="font-semibold text-slate-700">
                          {h.action === "cancel" ? "Cancelled" : h.action === "status" ? `Status → ${h.new_status}` : "Edited"} · {h.by} <span className="font-normal text-slate-400">({h.by_role})</span>
                        </p>
                        <p className="text-[11px] text-slate-400">{new Date(h.at).toLocaleString("en-IN")}</p>
                        {h.reason && <p className="mt-0.5 text-red-600">Reason: {h.reason}</p>}
                        {(h.changes || []).map((c, ci) => <p key={ci} className="mt-0.5">• {c}</p>)}
                        {(h.old_total != null && h.new_total != null) && <p className="mt-0.5 text-slate-500">Total: {formatMoney(h.old_total)} → <b>{formatMoney(h.new_total)}</b></p>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
